import axiosInstance from './axios';

// Type definitions matching backend models
export interface PatientDocument {
  documentId: string;
  filename: string;
  contentType: string;
  uploadedAt: string;
  s3Key: string;
}

export interface PatientAssessment {
  assessmentId: string;
  appointmentId: string;
  patientEmail: string;
  templateId: string;
  
  // Response data
  responses: Record<string, string>;
  totalScore?: number;
  scoreLevel?: string; // "minimal", "mild", "moderate", "severe"
  scoreInterpretation?: string;
  
  // Document uploads
  uploadedDocuments?: PatientDocument[];
  
  // Status tracking
  status: 'assigned' | 'in_progress' | 'completed';
  progress: number; // 0.0 to 1.0
  assignedAt: string;
  startedAt?: string;
  completedAt?: string;
  
  // Portal tracking
  emailSent: boolean;
  emailSentAt?: string;
  portalAccessCount: number;
  lastAccessedAt?: string;
}

export interface AssessmentSummary {
  appointmentId: string;
  patientEmail: string;
  totalAssessments: number;
  completedAssessments: number;
  
  // Clinical summary
  phq9Score?: number;
  phq9Level?: string;
  gad7Score?: number;
  gad7Level?: string;
  clinicalNotes?: string;
  
  // FMLA eligibility
  fmlaEligible?: boolean;
  fmlaReason?: string;
  recommendedDuration?: string;
}

export interface FMLAFormRequest {
  appointmentId: string;
  diagnosis?: string;
  clinicalFindings?: string;
  functionalLimitations?: string;
  workRestrictions?: string;
  startDate?: string;
  estimatedEndDate?: string;
}

export interface FMLAFormResponse {
  formId: string;
  pdfUrl: string;
  validationCode: string;
  generatedAt: string;
}

// Provider Assessment API Service
export const providerAssessmentAPI = {
  /**
   * Get all patient assessments for a specific appointment
   * Requires provider authentication and access to the appointment
   */
  getPatientAssessments: (appointmentId: string) =>
    axiosInstance.get<PatientAssessment[]>(`/provider/patients/${appointmentId}/assessments`),

  /**
   * Get clinical summary for provider review
   * Aggregates assessment results into a provider-friendly format
   */
  getAssessmentSummary: (appointmentId: string) =>
    axiosInstance.get<AssessmentSummary>(`/provider/assessments/${appointmentId}/summary`),

  /**
   * Generate FMLA certification form
   * Creates official DOL-compliant FMLA certification
   */
  generateFMLAForm: (appointmentId: string, formData: FMLAFormRequest) =>
    axiosInstance.post<FMLAFormResponse>(`/provider/assessments/${appointmentId}/fmla-form`, formData),

  /**
   * Download assessment documents
   * Gets secure download URL for patient-uploaded documents
   */
  getDocumentDownloadUrl: (appointmentId: string, documentId: string) =>
    axiosInstance.get<{ downloadUrl: string }>(`/provider/patients/${appointmentId}/documents/${documentId}/download`),

  /**
   * Send FMLA form to patient via email
   * Delivers the generated FMLA certification to patient and optionally to employer
   */
  sendFMLAFormByEmail: (appointmentId: string, formId: string, recipients: {
    patientEmail: string;
    employerEmail?: string;
    includePatient?: boolean;
    includeEmployer?: boolean;
  }) =>
    axiosInstance.post(`/provider/assessments/${appointmentId}/fmla-form/${formId}/send`, recipients),
};

// Utility functions for assessment analysis
export const assessmentUtils = {
  /**
   * Determine if patient assessments indicate FMLA eligibility
   */
  calculateFMLAEligibility: (assessments: PatientAssessment[]): boolean => {
    const phq9Assessment = assessments.find(a => a.templateId === 'phq9');
    const gad7Assessment = assessments.find(a => a.templateId === 'gad7');
    
    // Basic eligibility: PHQ-9 >= 10 OR GAD-7 >= 10
    const phq9Score = phq9Assessment?.totalScore ?? 0;
    const gad7Score = gad7Assessment?.totalScore ?? 0;
    
    return phq9Score >= 10 || gad7Score >= 10;
  },

  /**
   * Get completion status summary
   */
  getCompletionSummary: (assessments: PatientAssessment[]) => {
    const total = assessments.length;
    const completed = assessments.filter(a => a.status === 'completed').length;
    const percentage = total > 0 ? (completed / total) * 100 : 0;
    
    return { total, completed, percentage };
  },

  /**
   * Get assessment by template type
   */
  getAssessmentByType: (assessments: PatientAssessment[], templateId: string): PatientAssessment | undefined => {
    return assessments.find(a => a.templateId === templateId);
  },

  /**
   * Format score level for display
   */
  formatScoreLevel: (level?: string): string => {
    if (!level) return 'Not assessed';
    return level.charAt(0).toUpperCase() + level.slice(1);
  },

  /**
   * Get severity color for UI
   */
  getSeverityColor: (level?: string): 'success' | 'info' | 'warning' | 'error' => {
    switch (level) {
      case 'minimal': return 'success';
      case 'mild': return 'info';
      case 'moderate': return 'warning';
      case 'severe': return 'error';
      default: return 'info';
    }
  }
};